import { h, Component } from 'preact';
import { HopplerModalProps, HopplerModal } from '../common/hoppler-modal-component';
import { Listing } from '../../../../types/listing.model';
import { toDecimal } from '../../../../core/decimal';
import cx from 'classnames';

import { residentialPropTypes, LeaseTerms, FurnishingChoices, Amenities} from '../../../../constants/properties';
import { HopplerDatePicker } from '../common/hoppler-date-picker.component';
import { ListingEditor } from './listing-editor.component';
import { GET_LISTING_DETAIL, GET_LISTING_DATA, UPDATE_PROPERTY } from '../../reducers/listing/listing.actions';
import getStore, { appState$ } from '../../store';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { REQUEST_IN_PROGRESS, REQUEST_SUCCESS } from '../../../../types';
import { createKumitaListing } from '../../../utils/create-api-input';
import { Form } from '../../../utils/forms/form';

export interface ListingEditorModalProps extends HopplerModalProps {
  listings?: Listing[];
  focusedListing?: Listing;
  currentStage?: string;
  onModalStageChanged?: Function;
}

export interface ListingEditorModalState {
  listings?: Listing[];
  focusedListing?: Listing;

  isEditorVisible?: boolean;
  isGettingListingDetail?: boolean;
}

export class ListingEditorModal extends HopplerModal<ListingEditorModalProps, ListingEditorModalState> {
  getForm: Function;

  constructor(props: ListingEditorModalProps) {
    super(props);

    this.setState({
      listings: this.props.listings,
      isGettingListingDetail: false
    })
  }

  componentDidMount() {
    super.componentDidMount();
    const listings$ = appState$.pipe(
      map(app => app.listing)
    );
    
    listings$.pipe(
      map(listing => ({
          requestState: listing.getListingDetailState,
          focusedListing: listing.focusedListing
        })
      ),
      distinctUntilChanged()
    ).subscribe(({requestState, focusedListing}) => {
      // state is for spinners

      if (requestState == REQUEST_IN_PROGRESS) {
        this.setState({
          isGettingListingDetail: true
        });
      } else {
        this.setState({
          isEditorVisible: false
        });
        
        if (!this.state.focusedListing && requestState == REQUEST_SUCCESS) {
          this.setState({
            focusedListing: focusedListing,
            isEditorVisible: true
          });
        } else if (requestState == REQUEST_SUCCESS && focusedListing &&
            focusedListing.propertyKey !== this.state.focusedListing.propertyKey) {
          this.setState({
            focusedListing: focusedListing,
            isEditorVisible: true
          });
        }

        this.setState({
          isGettingListingDetail: false
        })
      }
      
    });
  }

  onReturnClicked = () => {
    this.setState({
      isEditorVisible: false,
      focusedListing: null
    });
  }

  doModalClose = () => {
    this.props.close();
  }

  onPropertyUpdateClicked = (e, propertyKey) => {
    this.setState({
      focusedListing: null,
      isEditorVisible: false
    });

    const store = getStore();
    store.dispatch({
      type: GET_LISTING_DETAIL,
      payload: {
        focusedPropertyKey: propertyKey
      }
    });
  }

  setOnsave = (passedFunction: Function) => {
    this.getForm = passedFunction;
  }

  onSave = () => {
    const data = this.getForm();
    const form: Form = data.form;
    const currentLocation= data.currentLocation;
    const locationForm: Form = data.locationForm;

    if (!form.isValid) {
      // for (let k in form.fields) {
      //   if (!form.fields[k].isValid) {
      //     console.log(k, form.fields[k].isValid);
      //   }
      // }
      return;
    }

    let kumitaListing = createKumitaListing(
      form,
      this.state.focusedListing,
      currentLocation,
      locationForm
    );

    const store = getStore();
    store.dispatch({
      type: UPDATE_PROPERTY,
      payload: {
        listingToUpdate: kumitaListing
      }
    });
  }

  onListingStageClicked = (stage: string) => {
    this.props.onModalStageChanged(stage, true);
    this.setState({
      isEditorVisible: false
    });
  }

  render(props: ListingEditorModalProps, state: ListingEditorModalState) {
    return (
      <div className="hi-listing-editor-modal container"
          ref={ node => this.refNode = node }>
        <div className="row h-100">
          <div className="col-12 col-lg-4 hi-listing-editor-modal__list">
            <div className="row d-lg-none mb-3">
              <div className="col-4 offset-8">
                <button className="btn btn-outline-dark w-100" onClick={this.doModalClose}>
                  Close
                </button>
              </div>
            </div>
            <div className="row hi-widget-collapsable__toggle-row">
              <div className="col-10">
                <h4 className="hi-offline-listing-editor-widget__name">
                  Offline Listings to Update
                </h4>
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                <p className="hi-offline-listing-editor-widget__desc">
                  { state.listings.length } Listings to update
                </p>
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-12">
                <button onClick={e => this.onListingStageClicked('pending')}
                  className={ cx(
                    'btn hi-offline-listing-editor-widget__cat-toggle mb-2', 
                    {
                      active: props.currentStage === 'pending'
                    }
                  )}>
                  Pending
                </button>
                <button onClick={e => this.onListingStageClicked('drafts')}
                  className={ cx(
                    'btn hi-offline-listing-editor-widget__cat-toggle mb-2', 
                    {
                      active: props.currentStage === 'drafts'
                    }
                  )}>
                  Drafts
                </button>
                <button onClick={e => this.onListingStageClicked('declined')}
                  className={ cx(
                    'btn hi-offline-listing-editor-widget__cat-toggle mb-2', 
                    {
                      active: props.currentStage === 'declined'
                    }
                  )}>
                  Declined
                </button>
                <button onClick={e => this.onListingStageClicked('expired')}
                  className={ cx(
                    'btn hi-offline-listing-editor-widget__cat-toggle mb-2', 
                    {
                      active: props.currentStage === 'expired'
                    }
                  )}>
                  Expired
                </button>
              </div>
            </div>

            <div className="row hi-listing-editor-modal__list__listings mt-0 mt-lg-4 pr-4">
              {
                props.listings ? props.listings.map((listing, index) => {
                  return (
                    <div className="col-12 hi-offline-listing-editor-widget__listing">
                      <a href="#" onClick={ (e) => this.onPropertyUpdateClicked(e, listing.propertyKey) }
                        className="hi-offline-listing-editor-widget__listing__update-btn">
                        Update <i className="fas fa-chevron-right"></i>
                      </a>
                      <p className="hi-offline-listing-editor-widget__listing__property-key">
                        { listing.propertyKey }
                      </p>
                      <p className="hi-offline-listing-editor-widget__listing__cat-type">
                        { listing.type } for { listing.category }
                      </p>
                      <p className="hi-offline-listing-editor-widget__listing__address">
                        { listing.address }
                      </p>
                      
                      {
                        listing.minPrice ? <p className="hi-offline-listing-editor-widget__listing__price">
                          Php { toDecimal(listing.minPrice) }{ listing.category == 'Rent' ? '/month' : '' }
                        </p> : null
                      }

                    </div>
                  );
                }) : null
              }

              {
                !props.listings || props.listings.length < 1 ?
                <div className="w-100 text-center">
                  <p>
                    No available listing data
                  </p>
                </div> : null
              }
            </div>

          </div>
          <div
              className={ cx(
                'col-12 col-lg-8',
                'hi-listing-editor-modal__editor-container',
                {
                  'mobile-visible': state.isEditorVisible
                }
              ) }>

            <div className="hi-listing-editor-modal__editor__actions">
              <div className="row align-items-center w-100">
                <h3 className="d-none d-lg-block col-3 hi-listing-editor-modal__editor__actions__h3 mb-0">
                  Listing Details
                </h3>

                <button className="col-2 btn d-lg-none" onClick={this.onReturnClicked}>
                  <i className="fas fa-arrow-left"></i>
                </button>

                <div className="col d-flex d-lg-none justify-content-between">
                  <button className="btn btn-secondary w-50" onClick={this.onSave}>
                    Save update
                  </button>
                  <button className="btn btn-outline-dark w-50 ml-2" onClick={this.doModalClose}>
                    Close
                  </button>
                </div> 
                
                <div className="col-9 d-none d-lg-flex justify-content-end">
                  <button className="btn btn-secondary mr-3" onClick={this.onSave}>
                    Save update
                  </button>
                  
                  <button className="btn btn-outline-dark" onClick={this.doModalClose}>
                    Close
                  </button>
                </div>      
              </div>
            </div>

            {/* <div className="row mt-4">
              <div className="col-12">
                <div className="hi-listing-editor-modal__editor__note px-3 py-2">
                  <b>
                    Information Needed
                  </b>
                  <p>
                    Kindly provide the following: Unit Number, Floor Area, Monthly Rental Price
                  </p>
                </div>
              </div>
            </div> */}

            <div className="row mt-4">
              {
                state.focusedListing ?
                <div className="col-12">
                  <ListingEditor 
                    setOnSave={this.setOnsave}
                    listing={state.focusedListing} />
                </div> : null
              }
            </div>

            <hr className="hi-listing-editor-modal__editor__contact-divider"/>

          </div>
        </div>
      </div>
    );
  }
}